/**
 * OrionSymbioseModal — Iter. 406.n
 *
 * Dopaminergic J7 celebration triggered when a user completes 7/7 daily
 * 7-day quests in the week. Renders a hand-crafted CSS/SVG constellation
 * "Symbiose Orion" — 7 golden stars (one per day validated) connect
 * dynamically to a central node, drawing the lines with stroke-dasharray
 * over ~2s. The center reveals the awarded points (200 base + 50/day).
 *
 * Trigger: parent (Dashboard) fetches /api/weekly-quest/pending-celebration
 * on mount and opens this modal when payload is non-null. On close, POST
 * /api/weekly-quest/celebration-shown so it never re-fires.
 *
 * Visual model
 * ────────────
 *   • Black backdrop with subtle starfield (CSS radial-gradient + dots).
 *   • 7 stars placed on a circle (angle = 360°/7).
 *   • Each star is a 4-point gold "twinkle" made from rotated CSS shapes.
 *   • SVG lines from each star → center, animated with stroke-dasharray.
 *   • Center is a large gold orb showing total bonus points.
 *   • CTA button → /dashboard.
 *
 * Re-usable: this is a single-shot celebration. Will not re-show once
 * dismissed; the parent reads the API-driven "shown" flag.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, ArrowRight, Trophy } from 'lucide-react';
import useCameraViewport from '../utils/useCameraViewport';

const RADIUS = 110;        // distance from center to each star
const CENTER = 130;        // viewBox center
const SIZE = 260;          // SVG viewBox size
const STAR_COUNT = 7;

function starPositions() {
  // Top of the circle = day 1 (Monday). Goes clockwise.
  const out = [];
  for (let i = 0; i < STAR_COUNT; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / STAR_COUNT;
    out.push({
      x: CENTER + RADIUS * Math.cos(angle),
      y: CENTER + RADIUS * Math.sin(angle),
      day: i + 1,
    });
  }
  return out;
}

export default function OrionSymbioseModal({
  open,
  onClose,
  celebration,         // { base_bonus, constancy_bonus, validated_days, total_bonus, earned_at }
  questDays = [],      // [{day_index, title, ...}, ...] — used to label stars
  cycleNumber = null,
  language = 'fr',
  // Iter. 406.p — switches the CTA toward AI plan creation for the
  // welcome-J7 transition into the first 6-week cycle.
  mode = 'weekly',     // 'weekly' | 'welcome_completion'
  onCreatePlan,        // () → void — invoked by the welcome-completion CTA
}) {
  const fr = language === 'fr';
  const navigate = useNavigate();
  const isWelcome = mode === 'welcome_completion';

  useCameraViewport({ open, lockBodyScroll: true });

  if (!open || !celebration) return null;

  const stars = starPositions();
  const base = celebration.base_bonus ?? 0;
  const constancy = celebration.constancy_bonus ?? 0;
  const total = celebration.total_bonus ?? base + constancy;
  const days = celebration.validated_days ?? 7;

  // Iter. 406.dd — DEFINITIVE FIX. Three issues survived 406.bb:
  //   1) Bottom nav still visible → the modal was rendered inside the
  //      Dashboard tree which lives under <Layout>. Any parent with a
  //      `transform` / `filter` / `will-change` creates a new stacking
  //      context that traps even `z-[max]`. Solution: `createPortal` to
  //      `document.body` so the modal escapes every parent.
  //   2) Content stuck at the BOTTOM of the screen → my last wrapper had
  //      `items-stretch` + an overflow-y-auto child but no defined height
  //      propagation, so `justify-center` on the inner div didn't fire.
  //      Solution: a single `flex flex-col` overlay with a centered
  //      `overflow-y-auto` content area using `100dvh`.
  //   3) Bottom nav bleeding through z-stack on some Android browsers →
  //      explicit `position: fixed` + inline `height: 100dvh` + max safe
  //      z-index makes it bullet-proof.
  const overlay = (
    <div
      className="orion-overlay-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        height: '100dvh',
        width: '100vw',
        backgroundColor: '#000',
        backgroundImage:
          'radial-gradient(circle at 50% 40%, rgba(60,40,10,0.7) 0%, rgba(0,0,0,0.98) 65%)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        display: 'flex',
        flexDirection: 'column',
        overscrollBehavior: 'contain',
      }}
      data-testid="orion-symbiose-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orion-title"
    >
      {/* Starfield background */}
      <div className="absolute inset-0 pointer-events-none orion-starfield" aria-hidden="true" />

      <button
        type="button"
        onClick={onClose}
        aria-label={fr ? 'Fermer' : 'Close'}
        data-testid="orion-close"
        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Centered, scroll-on-overflow content column. flex-1 grabs the
          remaining height, overflow-y-auto kicks in only on tiny screens. */}
      <div
        className="relative flex-1 w-full overflow-y-auto flex items-center justify-center"
        style={{ minHeight: 0 }}
      >
      <div className="relative w-full max-w-md mx-auto px-6 py-6 flex flex-col items-center text-center">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 orion-fade-in" style={{ animationDelay: '0.5s' }}>
          <Sparkles className="w-4 h-4 text-amber-300" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-200 font-bold">
            {fr ? 'Symbiose Orion' : 'Orion Symbiosis'}
          </p>
          <Sparkles className="w-4 h-4 text-amber-300" />
        </div>

        <h2
          id="orion-title"
          className={`font-heading uppercase text-white leading-tight mb-1 orion-rise-in ${
            // Iter. 406.bb — The welcome-completion variant ships a longer
            // title in French ("Ta semaine d'introduction est scellée") that
            // wraps to 2 lines and looked cramped at text-3xl. We size it
            // down to keep the visual hierarchy clean across phones.
            isWelcome ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'
          }`}
          style={{ animationDelay: '0.6s' }}
        >
          {isWelcome
            ? (fr ? 'Ta semaine d\'introduction est scellée' : 'Your intro week is sealed')
            : (fr ? 'Tu as scellé ta semaine' : 'You sealed your week')}
        </h2>
        {cycleNumber && (
          <p className="text-xs text-white/60 mb-6 tracking-wider">
            {fr ? `CYCLE ${cycleNumber} • ${days}/7 JOURS` : `CYCLE ${cycleNumber} • ${days}/7 DAYS`}
          </p>
        )}

        {/* Constellation */}
        <div className="relative w-full aspect-square max-w-[240px] sm:max-w-[300px] mx-auto" data-testid="orion-constellation">
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full h-full">
            {/* Lines from each star to the center, animated */}
            {stars.map((s) => (
              <line
                key={`l-${s.day}`}
                x1={s.x}
                y1={s.y}
                x2={CENTER}
                y2={CENTER}
                stroke="url(#orionGold)"
                strokeWidth="1.6"
                strokeLinecap="round"
                className="orion-line orion-line-draw"
                data-testid={`orion-line-day-${s.day}`}
                style={{ animationDelay: `${0.7 + s.day * 0.1}s` }}
              />
            ))}

            {/* Outer ring */}
            <circle
              cx={CENTER} cy={CENTER} r={RADIUS}
              fill="none" stroke="rgba(251,191,36,0.12)" strokeWidth="0.8"
              strokeDasharray="3 5"
            />

            {/* Each star (twinkle) */}
            {stars.map((s) => (
              <g
                key={`s-${s.day}`}
                transform={`translate(${s.x}, ${s.y})`}
                className="orion-star orion-fade-in"
                data-testid={`orion-star-day-${s.day}`}
                style={{ animationDelay: `${0.5 + s.day * 0.1}s` }}
              >
                <circle r="3.5" fill="#fde68a" />
                <circle r="7" fill="rgba(251,191,36,0.35)" />
                <circle r="12" fill="rgba(251,191,36,0.12)" />
                <text
                  x="0" y="22"
                  textAnchor="middle"
                  fontSize="8"
                  fill="rgba(255,255,255,0.6)"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                  letterSpacing="1"
                >
                  J{s.day}
                </text>
              </g>
            ))}

            {/* Center orb */}
            <g
              transform={`translate(${CENTER}, ${CENTER})`}
              className="orion-center-pop"
              style={{ animationDelay: '1.8s' }}
            >
              <circle r="28" fill="rgba(251,191,36,0.18)" />
              <circle r="20" fill="rgba(251,191,36,0.32)" />
              <circle r="14" fill="#facc15" />
            </g>

            <defs>
              <linearGradient id="orionGold" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#fef3c7" />
                <stop offset="50%" stopColor="#facc15" />
                <stop offset="100%" stopColor="#b45309" />
              </linearGradient>
            </defs>
          </svg>

          {/* Total bonus overlay — drawn over the center orb */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none orion-fade-in"
            style={{ animationDelay: '1.8s' }}
            data-testid="orion-points-total"
          >
            <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold leading-none">
              {fr ? 'Bonus J7' : 'J7 Bonus'}
            </p>
            <p className="text-4xl font-black text-amber-100 leading-none mt-1 drop-shadow-[0_0_18px_rgba(251,191,36,0.6)]">
              +{total}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-amber-200/70 mt-0.5">pts</p>
          </div>
        </div>

        {/* Points breakdown */}
        <div
          className="mt-2 w-full max-w-xs space-y-2 orion-fade-in"
          style={{ animationDelay: '2.8s' }}
          data-testid="orion-points-breakdown"
        >
          <div className="flex items-center justify-between text-xs text-white/80">
            <span>{fr ? 'Bonus complétion 7/7' : 'Completion bonus 7/7'}</span>
            <span className="font-bold text-amber-200">+{base}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-white/80">
            <span>
              {fr
                ? `Bonus constance (${days} jour${days > 1 ? 's' : ''} × 50pts)`
                : `Constancy bonus (${days} day${days > 1 ? 's' : ''} × 50pts)`}
            </span>
            <span className="font-bold text-amber-200">+{constancy}</span>
          </div>
          {celebration.multiplier && celebration.multiplier > 1 && (
            <div
              className="flex items-center justify-between text-xs text-amber-100"
              data-testid="orion-streak-multiplier"
            >
              <span>
                {fr
                  ? `Bonus Constance × ${celebration.multiplier} (${celebration.streak} sem. d'affilee)`
                  : `Constancy Bonus × ${celebration.multiplier} (${celebration.streak} weeks in a row)`}
              </span>
              <span className="font-bold text-amber-200">+{celebration.multiplier_extra || 0}</span>
            </div>
          )}
          <div className="h-px bg-amber-400/30" />
          <div className="flex items-center justify-between text-sm">
            <span className="font-heading uppercase tracking-wider text-white">{fr ? 'Total ajouté' : 'Total awarded'}</span>
            <span className="font-black text-amber-200">+{total} pts</span>
          </div>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={() => {
            if (isWelcome) {
              onClose();
              if (typeof onCreatePlan === 'function') onCreatePlan();
            } else {
              onClose();
              navigate('/dashboard');
            }
          }}
          data-testid="orion-cta"
          className="orion-cta-fade mt-7 w-full max-w-xs h-12 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-black font-heading uppercase tracking-widest text-sm flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
          style={{ animationDelay: '1.6s', boxShadow: '0 0 40px rgba(251,191,36,0.55)' }}
        >
          <Trophy className="w-4 h-4" />
          {isWelcome
            ? (fr ? 'Concevoir mon plan 6 semaines' : 'Build my 6-week plan')
            : (fr ? 'Retour au tableau de bord' : 'Back to dashboard')}
          <ArrowRight className="w-4 h-4" />
        </button>

        {isWelcome && (
          <button
            type="button"
            onClick={onClose}
            data-testid="orion-secondary-later"
            className="orion-cta-fade mt-3 text-[11px] uppercase tracking-widest text-white/60 hover:text-white/90 underline underline-offset-4"
            style={{ animationDelay: '1.7s' }}
          >
            {fr ? 'Plus tard' : 'Later'}
          </button>
        )}

        {/* Cycle ribbon */}
        {questDays && questDays.length === 7 && (
          <p
            className="mt-5 text-[10px] uppercase tracking-widest text-white/40 max-w-xs leading-relaxed orion-fade-in"
            style={{ animationDelay: '3.2s' }}
          >
            {isWelcome
              ? (fr
                  ? 'Active ton premier cycle de 6 semaines pour débloquer la boucle hebdomadaire.'
                  : 'Activate your first 6-week cycle to unlock the weekly loop.')
              : (fr
                  ? 'Un nouveau plan d\'attaque sera publié automatiquement lundi prochain.'
                  : 'A fresh 7-day quest will be published next Monday.')}
          </p>
        )}
      </div>
      </div>

      {/* Local styles for the starfield + twinkle pulse + entrance animations */}
      <style>{`
        .orion-starfield {
          background-image:
            radial-gradient(1px 1px at 12% 18%, rgba(255,255,255,0.7), transparent 60%),
            radial-gradient(1px 1px at 78% 23%, rgba(255,255,255,0.55), transparent 60%),
            radial-gradient(1.5px 1.5px at 35% 70%, rgba(251,191,36,0.7), transparent 60%),
            radial-gradient(1px 1px at 88% 80%, rgba(255,255,255,0.5), transparent 60%),
            radial-gradient(1px 1px at 22% 88%, rgba(255,255,255,0.4), transparent 60%),
            radial-gradient(1.5px 1.5px at 64% 55%, rgba(251,191,36,0.5), transparent 60%),
            radial-gradient(1px 1px at 50% 12%, rgba(255,255,255,0.6), transparent 60%);
          opacity: 0.85;
        }
        .orion-star circle:nth-child(1) {
          animation: orion-twinkle 2.2s ease-in-out infinite;
        }
        @keyframes orion-twinkle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.65; }
        }
        .orion-line {
          filter: drop-shadow(0 0 4px rgba(251,191,36,0.55));
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          opacity: 0;
        }
        .orion-line-draw {
          animation: orion-line-draw 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes orion-line-draw {
          0% { stroke-dashoffset: 300; opacity: 0; }
          15% { opacity: 0.85; }
          100% { stroke-dashoffset: 0; opacity: 0.85; }
        }
        .orion-fade-in {
          opacity: 0;
          animation: orion-fade-in 0.7s ease forwards;
        }
        @keyframes orion-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .orion-rise-in {
          opacity: 0;
          transform: translateY(16px);
          animation: orion-rise-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes orion-rise-in {
          0% { opacity: 0; transform: translateY(16px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .orion-center-pop {
          opacity: 0;
          transform-origin: center;
          transform-box: fill-box;
          transform: scale(0.55);
          animation: orion-center-pop 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes orion-center-pop {
          0% { opacity: 0; transform: scale(0.55); }
          70% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        .orion-cta-fade {
          opacity: 0.3;
          pointer-events: none;
          animation: orion-cta-arm 0.5s ease forwards;
        }
        @keyframes orion-cta-arm {
          0% { opacity: 0.3; pointer-events: none; }
          100% { opacity: 1; pointer-events: auto; }
        }
      `}</style>
    </div>
  );

  // Portal to <body> so the modal escapes every parent stacking context
  // (Layout's bottom nav, transform parents, etc.). SSR-safe with the
  // `typeof document` guard for Capacitor / test runners.
  if (typeof document === 'undefined') return overlay;
  return createPortal(overlay, document.body);
}
